import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import Container from "../../components/Container";
import Page from "../../components/Page";
import Spinner from "../../components/ui/spinner";
import { adminWorkspacesApi } from "../api/workspaces";
import { ApiError } from "../api/utils";
import type { AdminWorkspace } from "../modules/types";

const WorkspacesPage: React.FunctionComponent = () => {
  const [workspaces, setWorkspaces] = useState<AdminWorkspace[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    adminWorkspacesApi
      .getAll()
      .then((data) => {
        if (isMounted) setWorkspaces(data);
      })
      .catch((err: unknown) => {
        if (!isMounted) return;
        setError(
          err instanceof ApiError ? err.message : "Failed to load workspaces",
        );
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Page>
      <Container>
        <Heading level={1} variant="lg">Workspaces</Heading>
        <Text variant="xl" className="text-muted-foreground mb-6">
          All workspaces in the system
        </Text>

        {error && (
          <div className="bg-destructive/10 border border-destructive/40 px-4 py-3 rounded mb-4">
            <Text variant="sm" className="text-destructive">{error}</Text>
          </div>
        )}

        {workspaces === null && !error && (
          <div className="flex justify-center py-12">
            <Spinner size={32} className="text-muted-foreground" />
          </div>
        )}

        {workspaces && workspaces.length === 0 && (
          <Text variant="md" className="text-muted-foreground">
            No workspaces found.
          </Text>
        )}

        {workspaces && workspaces.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {workspaces.map((workspace) => (
              <Link
                key={workspace.id}
                to={`/admin/workspaces/${workspace.id}`}
                className="block hover:opacity-80 transition-opacity"
              >
                <Card>
                  <CardHeader>
                    <CardTitle>{workspace.name}</CardTitle>
                    <CardDescription>{workspace.slug}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Text variant="sm" className="text-muted-foreground">
                      {workspace.timezone || "No timezone set"}
                    </Text>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </Container>
    </Page>
  );
};

export default WorkspacesPage;
